/**
 * SessionHeader — Editable session name + comment count displayed at
 * the top of the feedback panel.
 */
import { useState, useRef, useEffect } from "react";

interface SessionHeaderProps {
  readonly sessionName: string;
  readonly commentCount: number;
  readonly onNameChange: (name: string) => void;
}

export function SessionHeader({
  sessionName,
  commentCount,
  onNameChange,
}: SessionHeaderProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(sessionName);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== sessionName) {
      onNameChange(trimmed);
    } else {
      setDraft(sessionName);
    }
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") commit();
    if (e.key === "Escape") {
      setDraft(sessionName);
      setEditing(false);
    }
  };

  return (
    <div className="fb-session-header">
      {editing ? (
        <input
          ref={inputRef}
          className="fb-session-name-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={handleKeyDown}
          aria-label="Session name"
          maxLength={80}
        />
      ) : (
        <button
          type="button"
          className="fb-session-name-btn"
          onClick={() => setEditing(true)}
          title="Click to rename session"
        >
          {sessionName}
          <span className="fb-edit-icon" aria-hidden="true">
            ✏️
          </span>
        </button>
      )}
      <span className="fb-comment-count">
        {commentCount} comment{commentCount !== 1 ? "s" : ""}
      </span>
    </div>
  );
}
